import { defineComponent, h } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { container, pageTitle } from '@/ui/components';
import { IdProps } from '@/ui/helpers/uiHelper';
import { hasPermission, Permissions } from '@/ui/auth/auth';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { queryTabName, UserEditTabNames } from '@/ui/components/navigation/constants';
import { secondaryNavbar, TabItem } from '@/ui/components/navigation/SecondaryNavbar';
import * as qs from '@/ui/queryState';
import { UserEditGeneralTab } from '@/ui/pages/user/UserEditGeneralTab';
import { ReminderConfigurations } from '@/ui/components/reminderNotification/Configurations';
import UserEditPasswordTab from '@/ui/pages/user/UserEditPasswordTab.vue';

export const UserEditPage = defineComponent<IdProps>({
  name: 'UserEditPage',
  props: ['id'],
  setup({ id }) {
    if (!hasPermission(Permissions.MANAGE_USERS)) {
      return renderForbiddenPage;
    }

    const activeTab = qs.stringEnum<UserEditTabNames>(
      queryTabName,
      Object.values(UserEditTabNames),
      UserEditTabNames.GENERAL,
      false,
    );

    const secondaryTabItems : TabItem[] = [
      {
        value: UserEditTabNames.GENERAL,
        label: t('common.general'),
        icon: 'user',
        underLineColor: 'blue',
      },
      {
        value: UserEditTabNames.CHANGE_PASSWORD,
        label: t('common.change-password'),
        icon: 'lock',
        underLineColor: 'purple',
      },
      {
        value: UserEditTabNames.NOTIFICATIONS,
        label: t('common.notifications'),
        icon: 'bell',
        underLineColor: 'green',
      },
    ];

    const getTab = () => {
      switch (activeTab.value) {
        case UserEditTabNames.GENERAL:
          return h(UserEditGeneralTab, { props: { id } });
        case UserEditTabNames.CHANGE_PASSWORD:
          return h(UserEditPasswordTab, { props: { id } });
        case UserEditTabNames.NOTIFICATIONS:
          return h(ReminderConfigurations, { props: { id, pageType: 'user' } });
        default:
          throw new Error('Invalid tab selected. Please use UserEditTabNames enumeration');
      }
    };

    return function render() {
      return container(
        [
          pageTitle(t('user.edit')),

          secondaryNavbar({
            props: { value: activeTab.value, tabItems: secondaryTabItems },
            on: {
              input: (value: UserEditTabNames) => {
                activeTab.value = value;
              },
            },
          }),
          getTab(),
        ],
        'narrow',
      );
    };
  },
});
